import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import NotificationBell from '../NotificationBell';
import { useTheme, THEMES } from '../../context/ThemeContext';
import { API_URL } from '../../config/api';
import { hasPermission, clearPermissions } from '../../utils/permissions';

/**
 * TopBar — the shell's top navigation strip.
 *
 * This is a fidelity port of the original Header component with a single
 * visual change: emerald background + white text/icons instead of white
 * background + gray/emerald text. Everything else — search with type
 * selector (candidates/searches), typeahead dropdown, theme toggle,
 * notification bell, profile menu with all items (My Profile, User
 * Management, Export, Import, Sign Out), the actual SVG logo — is
 * preserved from the original Header.
 *
 * The rewrite that shipped previously dropped features. That was a
 * mistake. This version restores everything and only changes colors.
 *
 * Props (optional, per page):
 *   onAddClick   — page-specific add button (Firms page shows "Add Firm")
 *   onExport     — page-specific export handler; surfaces in profile menu
 *                  when both prop present AND permission
 *   onImport     — page-specific import handler; same visibility rule
 *   variant      — 'recruiter' | 'client', for future symmetry
 */
export default function TopBar({ onAddClick, onExport, onImport, variant = 'recruiter', taskCount = null, onAssistantClick, assistantOpen = false, onUtilityClick, utilityView = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.user_type === 'admin';

  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('candidates');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchRef.current && !searchRef.current.contains(e.target) &&
        resultsRef.current && !resultsRef.current.contains(e.target)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    const timer = setTimeout(() => performSearch(), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchType]);

  const performSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const token = localStorage.getItem('authToken');
      if (searchType === 'candidates') {
        const response = await axios.get(
          `${API_URL}/candidate-archive/internal-archive?search=${encodeURIComponent(searchQuery)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSearchResults((response.data || []).slice(0, 8));
      } else {
        const response = await axios.get(`${API_URL}/pipelines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const filtered = response.data
          .filter(p =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.firm_name?.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .slice(0, 8);
        setSearchResults(filtered);
      }
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (result) => {
    if (searchType === 'candidates') {
      navigate(`/candidates?candidate=${result.candidate_id}`);
    } else {
      navigate(`/search/${result.id}`);
    }
    setShowResults(false);
    setSearchQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      setSearchQuery('');
    } else if (e.key === 'Enter' && searchQuery.trim()) {
      if (searchType === 'candidates') {
        navigate(`/candidates?search=${encodeURIComponent(searchQuery)}`);
      } else {
        navigate(`/searches?search=${encodeURIComponent(searchQuery)}`);
      }
      setShowResults(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    clearPermissions();
    window.location.href = variant === 'client' ? '/client/login' : '/login';
  };

  const isActive = (path) => {
    // "/" must be exact-match; startsWith('/') is always true for any
    // URL, which is why Dashboard was highlighting on every page.
    if (path === '/') return location.pathname === '/';
    // Candidates nav: primary URL is /candidates, but also match the
    // legacy /candidates-expanded route (for backward compat) and any
    // candidate detail page. Do NOT include '/' here — Dashboard owns
    // that now.
    if (path === '/candidates') return location.pathname === '/candidates' || location.pathname === '/candidates-expanded' || location.pathname.startsWith('/candidate/');
    if (path === '/firms') return location.pathname === '/firms' || location.pathname.startsWith('/firm/');
    if (path === '/searches') return location.pathname === '/searches' || location.pathname.startsWith('/search/');
    return location.pathname.startsWith(path);
  };

  // Nav pill styling — active gets a white overlay on the emerald;
  // inactive is slightly muted white text with a subtle white overlay
  // on hover. Same visual language we've used on the mini-sidebar
  // treatments (white/10 hover, white/20 active) so nothing looks
  // out-of-family across surfaces.
  const navLinkClass = (path) =>
    `px-2.5 py-1 text-xs font-medium rounded transition-colors ${
      isActive(path)
        ? 'bg-white/20 text-white'
        : 'text-white/85 hover:bg-white/10 hover:text-white'
    }`;

  const initials = (user.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const isDark = theme === THEMES.dark;

  return (
    <header className="bg-emerald-800 sticky top-0 z-40 h-[44px] flex-shrink-0">
      <div className="max-w-full px-4 h-full flex items-center">

        {/* Left: Assistant launcher + Nav. The old brand logo is now an
            assistant button — clicking it opens the inline left sidebar
            (chat / tools / tasks). A red badge shows the open task count so
            the assistant is instrumental and always surfaces pending work. */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={onAssistantClick}
            aria-label="Open assistant"
            title="Assistant"
            className={`relative w-8 h-8 flex-shrink-0 rounded-lg flex items-center justify-center transition-colors ${assistantOpen ? 'bg-white/25' : 'hover:bg-white/15'}`}
          >
            {assistantOpen ? (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.29 3.5a.75.75 0 011.42 0l1.63 4.9a2 2 0 001.26 1.26l4.9 1.63a.75.75 0 010 1.42l-4.9 1.63a2 2 0 00-1.26 1.26l-1.63 4.9a.75.75 0 01-1.42 0l-1.63-4.9a2 2 0 00-1.26-1.26l-4.9-1.63a.75.75 0 010-1.42l4.9-1.63a2 2 0 001.26-1.26l1.63-4.9z" />
                <path d="M5 3a.4.4 0 01.76 0l.43 1.3a1 1 0 00.63.63l1.3.43a.4.4 0 010 .76l-1.3.43a1 1 0 00-.63.63l-.43 1.3a.4.4 0 01-.76 0l-.43-1.3a1 1 0 00-.63-.63l-1.3-.43a.4.4 0 010-.76l1.3-.43a1 1 0 00.63-.63L5 3z" />
              </svg>
            )}
          </button>
          <nav className="flex items-center gap-0.5">
            {variant === 'client' ? (
              <>
                <button onClick={() => navigate('/client/dashboard')} className={navLinkClass('/client/dashboard')}>Dashboard</button>
                <button onClick={() => navigate('/client/archive')} className={navLinkClass('/client/archive')}>Candidates</button>
                <button onClick={() => navigate('/client/tags')} className={navLinkClass('/client/tags')}>Tags</button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/')} className={navLinkClass('/')}>Dashboard</button>
                <button onClick={() => navigate('/candidates')} className={navLinkClass('/candidates')}>Candidates</button>
                <button onClick={() => navigate('/searches')} className={navLinkClass('/searches')}>Searches</button>
                <button onClick={() => navigate('/outreach')} className={navLinkClass('/outreach')}>Outreach</button>
                <button onClick={() => navigate('/firms')} className={navLinkClass('/firms')}>Firms</button>
                {isAdmin && <button onClick={() => navigate('/users')} className={navLinkClass('/users')}>Users</button>}
              </>
            )}
          </nav>
        </div>

        {/* Spacer */}
        <div className="flex-1 min-w-[40px]" />

        {/* Right cluster: Search + actions */}
        <div className="flex items-center gap-2">

          {/* Search — kept as-is from the original: white/gray input surface
              on the emerald bg reads clearly as an input field. Type selector
              between Candidates and Searches preserved. */}
          <div className="relative w-64" ref={searchRef}>
            <div className="flex items-center bg-white/95 border border-white/20 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-white focus-within:border-white">
              <select
                value={searchType}
                onChange={(e) => { setSearchType(e.target.value); setSearchResults([]); }}
                className="bg-transparent text-[11px] text-gray-500 pl-2 pr-0.5 py-1 border-r border-gray-200 focus:outline-none cursor-pointer"
              >
                <option value="candidates">Candidates</option>
                <option value="searches">Searches</option>
              </select>
              <div className="flex-1 flex items-center">
                <svg className="w-3.5 h-3.5 text-gray-400 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.trim() && setShowResults(true)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search..."
                  className="flex-1 bg-transparent text-xs px-1.5 py-1.5 focus:outline-none min-w-0"
                />
                {isSearching && (
                  <div className="mr-1.5">
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-300 border-t-emerald-500"></div>
                  </div>
                )}
                {searchQuery && !isSearching && (
                  <button
                    onClick={() => { setSearchQuery(''); setSearchResults([]); setShowResults(false); }}
                    className="mr-1.5 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Search Results Dropdown */}
            {showResults && searchQuery.trim() && searchResults.length > 0 && (
              <div ref={resultsRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[400px] overflow-auto">
                {searchType === 'candidates' ? (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b bg-gray-50">Candidates ({searchResults.length})</div>
                    {searchResults.map((c) => (
                      <button key={c.candidate_id} onClick={() => handleResultClick(c)} className="w-full px-3 py-2 text-left hover:bg-emerald-50 flex items-center gap-3 border-b border-gray-100 last:border-0">
                        {c.profile_picture_url ? (
                          <img src={c.profile_picture_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium">{c.name?.charAt(0)?.toUpperCase()}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{c.name}</div>
                          <div className="text-xs text-gray-500 truncate">{c.current_title || 'No title'}{c.current_company && ` at ${c.current_company}`}</div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {c.linkedin && (
                            <a href={c.linkedin.startsWith('http') ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#0A66C2] hover:text-[#004182]">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                            </a>
                          )}
                          {c.email && <span className="text-gray-400" title="Has email"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></span>}
                        </div>
                      </button>
                    ))}
                    <button onClick={() => { navigate(`/candidates?search=${encodeURIComponent(searchQuery)}`); setShowResults(false); setSearchQuery(''); }} className="w-full px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-50 text-center font-medium">View all results in Candidates →</button>
                  </>
                ) : (
                  <>
                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b bg-gray-50">Searches ({searchResults.length})</div>
                    {searchResults.map((p) => (
                      <button key={p.id} onClick={() => handleResultClick(p)} className="w-full px-3 py-2 text-left hover:bg-emerald-50 flex items-center gap-3 border-b border-gray-100 last:border-0">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-700">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                          <div className="text-xs text-gray-500">{p.firm_name || 'No firm'} • {p.card_count ?? 0} candidates</div>
                        </div>
                      </button>
                    ))}
                    <button onClick={() => { navigate(`/searches?search=${encodeURIComponent(searchQuery)}`); setShowResults(false); setSearchQuery(''); }} className="w-full px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-50 text-center font-medium">View all in Searches →</button>
                  </>
                )}
              </div>
            )}

            {showResults && searchQuery.trim() && searchResults.length === 0 && !isSearching && (
              <div ref={resultsRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
                <p className="text-sm text-gray-500 text-center">No {searchType === 'candidates' ? 'candidates' : 'searches'} found for "{searchQuery}"</p>
              </div>
            )}
          </div>

          {/* Add button — page-specific, controlled by prop from AppShell */}
          {location.pathname === '/firms' && onAddClick && (
            <button onClick={onAddClick} className="px-2.5 py-1 text-xs bg-white text-emerald-800 rounded-lg hover:bg-white/90 flex items-center gap-1 font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add Firm
            </button>
          )}

          {/* Notification bell — matches theme-toggle styling for
              visual parity (same padding, hover, icon size, colors). */}
          <NotificationBell
            buttonClassName="relative p-1.5 rounded hover:bg-white/10 text-white/80 hover:text-white transition-colors"
            iconSize="w-6 h-6"
          />

          {/* Theme Toggle — restored */}
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded hover:bg-white/10 text-white/80 hover:text-white transition-colors"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>

          {/* Tools + Tasks — open the right-side utility sidebar. Icons only. */}
          <button
            onClick={() => onUtilityClick?.('tools')}
            title="Tools"
            aria-label="Tools"
            className={`p-1.5 rounded text-white/80 hover:text-white transition-colors ${utilityView === 'tools' ? 'bg-white/20' : 'hover:bg-white/10'}`}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
            </svg>
          </button>
          <button
            onClick={() => onUtilityClick?.('tasks')}
            title="Tasks"
            aria-label="Tasks"
            className={`relative p-1.5 rounded text-white/80 hover:text-white transition-colors ${utilityView === 'tasks' ? 'bg-white/20' : 'hover:bg-white/10'}`}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
            </svg>
            {taskCount != null && taskCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] px-1 flex items-center justify-center">
                {taskCount > 9 ? '9+' : taskCount}
              </span>
            )}
          </button>

          {/* Profile Avatar with full menu — restored */}
          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center text-[11px] font-semibold hover:ring-2 hover:ring-white/40 transition-all flex-shrink-0"
              style={{ color: '#064e3b' }}
              title={user.name || 'Profile'}
            >
              {initials}
            </button>
            {profileOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setProfileOpen(false)} />
                <div className="absolute right-0 mt-1.5 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  <div className="px-3 py-2 border-b">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email || ''}</p>
                  </div>
                  <button onClick={() => { navigate(variant === 'client' ? '/client/profile' : '/profile'); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    My Profile
                  </button>
                  {user.user_type !== 'client' && (
                    <button onClick={() => { navigate('/user-management'); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                      User Management
                    </button>
                  )}
                  {onExport && hasPermission('export_data') && (
                    <button onClick={() => { onExport(); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Export
                    </button>
                  )}
                  {onImport && hasPermission('import_candidates') && (
                    <button onClick={() => { onImport(); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 text-gray-700">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      Import
                    </button>
                  )}
                  <div className="border-t my-1"></div>
                  <button onClick={() => { handleLogout(); setProfileOpen(false); }} className="w-full px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
